import { ProposalDecision, proposalCount, } from '@debbie/common/dist/agents/index.js';
import { DAYS_PER_WEEK } from '@debbie/common/dist/util/dates.js';
import { chunk, sumBy } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { INSIGHTS_PALETTE } from '../Insights/GeneralManagement/palette.js';
import { useHighlightTooltip } from '../Insights/HighlightTooltip.js';
import Panel from '../Panel.js';
// Past a month, daily bars get too thin to read.
const MAX_DAILY_BARS = 31;
// Checked for color-vision-deficiency separation; the brand palette's green and
// mauve are too close to tell apart.
const ACCEPTED_COLOR = '#2A9D6F';
const REJECTED_COLOR = '#C2453E';
const PENDING_COLOR = '#4A7FD6';
// Grid lines to draw while there are no proposals yet.
const MIN_Y_AXIS_MAX = 4;
// Grows with its grid row, so the chart matches the height of the tiles.
const ChartPanel = styled(Panel) `
  display: flex;
  flex-direction: column;

  > div,
  > div > .body {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;
const ChartFrame = styled.div `
  position: relative;
  flex: 1;
  min-height: 240px;
`;
const EmptyMessage = styled.div `
  position: absolute;
  inset: 0 0 40px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${THEME.COLOR_GREY};
  font-size: 0.85em;
  pointer-events: none;
`;
const DecisionChart = ({ days }) => {
    const lang = useSelector((s) => s.lang);
    const { tooltipProps, barProps } = useHighlightTooltip();
    const series = [
        {
            name: lang.agentPerformance.accepted,
            color: ACCEPTED_COLOR,
            decisions: [ProposalDecision.ApprovedAsIs, ProposalDecision.Edited],
        },
        {
            name: lang.agentPerformance.decisions[ProposalDecision.Rejected],
            color: REJECTED_COLOR,
            decisions: [ProposalDecision.Rejected],
        },
        {
            name: lang.agentPerformance.decisions[ProposalDecision.Pending],
            color: PENDING_COLOR,
            decisions: [ProposalDecision.Pending],
        },
    ];
    const buckets = chunk(days, days.length > MAX_DAILY_BARS ? DAYS_PER_WEEK : 1).map((bucketDays) => {
        const first = moment(bucketDays[0].date);
        const last = moment(bucketDays[bucketDays.length - 1].date);
        return {
            label: first.format('D/M'),
            range: bucketDays.length === 1
                ? first.format('LL')
                : `${first.format('LL')} – ${last.format('LL')}`,
            ...Object.fromEntries(series.map((serie) => [
                serie.name,
                sumBy(bucketDays, (day) => sumBy(serie.decisions, (decision) => day.decisions[decision])),
            ])),
        };
    });
    const isEmpty = days.every((day) => proposalCount(day.decisions) === 0);
    return (React.createElement(ChartPanel, null,
        React.createElement(ChartFrame, null,
            isEmpty && (React.createElement(EmptyMessage, null, lang.agentPerformance.noProposals)),
            React.createElement(ResponsiveContainer, null,
                React.createElement(BarChart, { data: buckets, margin: { top: 10, right: 0, left: -20, bottom: 0 } },
                    React.createElement(CartesianGrid, { stroke: INSIGHTS_PALETTE.grid, strokeDasharray: "3 3", vertical: false }),
                    React.createElement(XAxis, { dataKey: "label", tick: { fontSize: 11, fill: INSIGHTS_PALETTE.textMuted }, tickLine: false }),
                    React.createElement(YAxis, { allowDecimals: false, domain: [0, (max) => Math.max(max, MIN_Y_AXIS_MAX)], tick: { fontSize: 11, fill: INSIGHTS_PALETTE.textMuted }, tickLine: false, axisLine: false }),
                    React.createElement(Tooltip, { ...tooltipProps, cursor: { fill: INSIGHTS_PALETTE.surfaceMuted }, labelFormatter: (_, payload) => payload[0]?.payload.range }),
                    React.createElement(Legend, { iconType: "square", wrapperStyle: { fontSize: 12 } }),
                    series.map((serie) => (React.createElement(Bar, { key: serie.name, dataKey: serie.name, stackId: "decisions", fill: serie.color, stroke: INSIGHTS_PALETTE.surface, strokeWidth: 1, ...barProps(serie.name) }))))))));
};
export default DecisionChart;
//# sourceMappingURL=DecisionChart.js.map